import { useState, useEffect } from "react";
import { ArrowButton, Button, Input } from "@common/index";
import { useMeasureSystem } from "@hooks/useMeasureSystem";
import styles from "./styles.module.scss";
import { UserService } from "@api/services/userService";
import { Tooltip } from "src/routes/onboarding/shared/components/tooltip";
import fingerup from "@assets/img/emoji/fingerup.png";
import { MeasurementsSwitch } from "src/routes/onboarding/shared/components/measurementsSwitch";

const title = "Calculating your body mass index";
const tooltipContent =
  "BMI is widely used as a risk factor for the development of or the prevalence of several health issues.";

const STORAGE_KEY = "userHeight";

export const Height = ({ onNextStep, onTrackEvent }) => {
  const { ms, changeMs } = useMeasureSystem();
  const [heightEu, setHeightEu] = useState();
  const [heightUS, setHeightUS] = useState({ ft: undefined, inch: undefined });
  const [isValid, setIsValid] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);

  // --- при первом рендере восстанавливаем данные ---
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);

      if (parsed.cm) {
        setHeightEu(parsed.cm);
        validateCm(parsed.cm);
      }
      if (parsed.ft !== undefined && parsed.inch !== undefined) {
        setHeightUS({ ft: parsed.ft, inch: parsed.inch });
        validateFtIn(parsed.ft, parsed.inch);
      }
    }
  }, []);

  // --- при смене системы конвертируем ---
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return;

    const parsed = JSON.parse(saved);

    if (ms === "eu" && parsed.ft !== undefined) {
      const cm = Math.round(parsed.ft * 30.48 + (parsed.inch || 0) * 2.54);
      setHeightEu(cm);
      validateCm(cm);
    }

    if (ms === "us" && parsed.cm !== undefined) {
      const ft = Math.floor(parsed.cm / 30.48);
      const inch = Math.round((parsed.cm - ft * 30.48) / 2.54);
      setHeightUS({ ft, inch });
      validateFtIn(ft, inch);
    }
  }, [ms]);

  const validateCm = (cm) => {
    setIsValid(cm <= 241 && cm >= 90);
  };

  const validateFtIn = (ft, inch) => {
    setIsValid(ft >= 3 && ft <= 7 && inch >= 0 && inch <= 11);
  };

  const handleChangeCm = (e) => {
    const cm = e.currentTarget.value;
    if (cm === "") {
      setHeightEu(undefined);
      setIsValid(false);
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    if (cm.length <= 3 && !isNaN(cm)) {
      const numericValue = Number(cm);
      setHeightEu(numericValue);
      validateCm(numericValue);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ cm: numericValue }));
    }
  };

  const handleChangeFt = (e) => {
    const ft = e.currentTarget.value;
    if (ft === "") {
      setHeightUS((prev) => ({ ...prev, ft: undefined }));
      setIsValid(false);
      return;
    }

    if (!isNaN(ft)) {
      const numericValue = Number(ft);
      const newState = { ...heightUS, ft: numericValue };
      setHeightUS(newState);
      validateFtIn(numericValue, newState.inch ?? 0);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
    }
  };

  const handleChangeInch = (e) => {
    const inch = e.currentTarget.value;
    if (inch === "") {
      setHeightUS((prev) => ({ ...prev, inch: undefined }));
      setIsValid(false);
      return;
    }

    if (!isNaN(inch)) {
      const numericValue = Number(inch);
      if (numericValue <= 11) {
        const newState = { ...heightUS, inch: numericValue };
        setHeightUS(newState);
        validateFtIn(newState.ft ?? 0, numericValue);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
      }
    }
  };

  const handleNextStep = async () => {
    const payload =
      ms === "us"
        ? { qqHeightFt: heightUS.ft, qqHeightIn: heightUS.inch || 0 }
        : { qqHeightCm: heightEu };

    const heightValue =
      ms === "us" ? `${heightUS.ft}'${heightUS.inch || 0}"` : `${heightEu} cm`;

    try {
      await UserService.updateQuestions(payload);
      onNextStep();
      onTrackEvent(heightValue);
    } catch (e) {
      console.log(e);
    }
  };

  const handleFocus = () => setShowTooltip(true);
  const handleBlur = () => setShowTooltip(true);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h2>How tall are you?</h2>
        <MeasurementsSwitch ms={ms} changeMs={changeMs} units={["ft", "cm"]} />
        <div className={styles.inputWrapper}>
          <div className={styles.inputContainer}>
            {ms === "eu" ? (
              <Input
                inputmode="numeric"
                type="number"
                step="1"
                placeholder={"0"}
                value={heightEu ?? ""}
                onChange={handleChangeCm}
                endAdornment={<h5>cm</h5>}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            ) : (
              <div className={styles.usInputs}>
                <Input
                  inputmode="numeric"
                  onChange={handleChangeFt}
                  type="number"
                  step="1"
                  value={heightUS.ft ?? ""}
                  placeholder={"0"}
                  endAdornment={<h5>ft</h5>}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
                <Input
                  inputmode="numeric"
                  value={heightUS.inch ?? ""}
                  onChange={handleChangeInch}
                  type="number"
                  step="1"
                  placeholder={"0"}
                  endAdornment={<h5>in</h5>}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
              </div>
            )}
            <ArrowButton
              variant="walky"
              isValid={isValid}
              handleNextStep={handleNextStep}
            />
          </div>
          <p className={styles.hint}>
            Please, enter a value from{" "}
            <span>{ms === "eu" ? `90 cm to 241 cm` : `3ft to 7ft 11 in`}</span>
          </p>
        </div>
        {showTooltip && (
          <Tooltip
            icon={fingerup}
            title={title}
            content={tooltipContent}
            background="walky"
          />
        )}
      </div>
      <div className={styles.action}>
        <Button variant="walky" onClick={handleNextStep} disabled={!isValid}>
          Continue
        </Button>
      </div>
    </div>
  );
};
